import { Skeleton } from '@mui/material'
import { isWithinInterval } from 'date-fns'
import { FormProvider, useForm } from 'react-hook-form'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'

import { useGetByTenantAccountAndAccountSourceAccountIdentifierQuery } from '@/api/rtk-api'
import {
  type SpeedDialRange,
  Card,
  DateRangePicker,
  last30Days,
  last90Days,
  lastYear,
  Tabs,
  yearToDate
} from '@/components/core-v2'
import { PATH, TENANT } from '@/const'
import { Currency } from '@/utilities/currency'

import { DeductionsTab, DeductionsTabLoading } from './deductions-tab'
import { TotalTab, TotalTabLoading } from './total-tab'
import { formatFeeOperations } from './utils'

export const TradeFeesBreakdownTab = () => {
  const { '*': tab, source, tradeId } = useParams()
  const form = useForm()
  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: tradeId,
    accountSource: source
  })

  if (isLoading) {
    return (
      <Tabs.Panel value="overview">
        <Card.Wrapper>
          <Skeleton className="mb-4" height="39px" width="320px" />
          <Tabs.List aria-label="Fees tabs" ruleClassName="mb-8" spacing="sm">
            <Tabs.Tab isSelected={tab === 'total'} value="total">
              Total fees during this period
            </Tabs.Tab>
            <Tabs.Tab isSelected={tab === 'deductions'} value="deductions">
              Fee deductions
            </Tabs.Tab>
          </Tabs.List>
          {tab === 'total' && <TotalTabLoading />}
          {tab === 'deductions' && <DeductionsTabLoading />}
        </Card.Wrapper>
      </Tabs.Panel>
    )
  }

  const { accountSnapShots } = data

  const [firstSnapshot] = accountSnapShots
  const lastSnapshot = accountSnapShots.at(-1)

  const defaultDateRange: [Date, Date] = [
    new Date(firstSnapshot.valuationDate),
    new Date(lastSnapshot.valuationDate)
  ]

  const dateRangeValue: [Date, Date] = form.watch('fee-date-picker', defaultDateRange)

  const snapshotsInRange = accountSnapShots.filter(snapshot =>
    isWithinInterval(new Date(snapshot.valuationDate), {
      start: new Date(dateRangeValue[0]),
      end: new Date(dateRangeValue[1])
    })
  )

  const beginningSpeedDial: SpeedDialRange = {
    label: 'Since the beginning',
    value: [defaultDateRange[0], defaultDateRange[1]]
  }

  const { history, total } = snapshotsInRange.reduce(
    (acc, snapshot) => {
      const formattedFees = formatFeeOperations(snapshot.operations as any)
      const noteFees = formattedFees?.note?.fees || 0
      const portfolioFees = formattedFees?.portfolio?.fees || 0
      const strategyFees = formattedFees?.strategy?.fees || 0
      const totalFees = noteFees + portfolioFees + strategyFees
      const newHistoryItem = {
        currency: snapshot.notionalCurrency as Currency,
        date: snapshot.valuationDate,
        split: formattedFees,
        fees: totalFees
      }

      if (totalFees > 0) {
        acc.history.push(newHistoryItem)
        acc.total.fees += totalFees
        acc.total.split.note.fees += noteFees
        acc.total.split.portfolio.fees += portfolioFees
        acc.total.split.strategy.fees += strategyFees
        const accumulateCategoryFees = (type: 'note' | 'portfolio' | 'strategy') =>
          formattedFees[type].split.forEach(category => {
            const categories = acc.total.split[type].split
            const existingCategory = categories.find(innerCat => innerCat.name === category.name)
            if (existingCategory) {
              existingCategory.fees += category.fees
            } else {
              categories.push({ ...category })
            }
          })
        const types = ['note', 'portfolio', 'strategy'] as const
        types.forEach(type => accumulateCategoryFees(type))
      }

      return acc
    },
    {
      history: [],
      total: {
        currency: lastSnapshot.notionalCurrency as Currency,
        fees: 0,
        split: {
          note: { name: 'Note', fees: 0, split: [] },
          portfolio: { name: 'Portfolio', fees: 0, split: [] },
          strategy: { name: 'Strategy', fees: 0, split: [] }
        }
      }
    }
  )

  return (
    <Tabs.Panel value="overview">
      <Card.Wrapper>
        <FormProvider {...form}>
          <DateRangePicker
            isOnlyMonthPicker
            isPopover
            className="mb-4"
            defaultValue={beginningSpeedDial.value}
            id="fee-date-picker"
            showClearDatesButton={false}
            speedDials={[beginningSpeedDial, last30Days, last90Days, lastYear, yearToDate]}
          />
          <Tabs.List aria-label="Fees tabs" ruleClassName="mb-8" spacing="sm">
            <Tabs.Tab
              isSelected={tab === 'total'}
              to={PATH.reportsSingleTradeFeesBreakdownTotal(source, tradeId)}
              value="total"
            >
              Total fees during this period
            </Tabs.Tab>
            <Tabs.Tab
              isSelected={tab === 'deductions'}
              to={PATH.reportsSingleTradeFeesBreakdownDeductions(source, tradeId)}
              value="deductions"
            >
              Fee deductions
            </Tabs.Tab>
          </Tabs.List>
          <Routes>
            <Route
              element={<Navigate to={PATH.reportsSingleTradeFeesBreakdownTotal(source, tradeId)} />}
              path=""
            />
            <Route element={<TotalTab data={total} />} path="total" />
            <Route element={<DeductionsTab data={history} />} path="deductions" />
          </Routes>
        </FormProvider>
      </Card.Wrapper>
    </Tabs.Panel>
  )
}
